import { useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { X, User, Building2, Mail, Phone, Users, MessageSquare, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../UI/Button';
import { APP_CONFIG } from '../../config/constants';

const BookDemoModal = ({ isOpen, onClose }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();

  // Store current form data for Calendly callback using ref
  const currentFormDataRef = useRef(null);
  const leadIdRef = useRef(null);
  const bookingCompletedRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      bookingCompletedRef.current = false;
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Send lead data to backend
  const submitLeadToBackend = async (data) => {
    const payload = {
      fullName: data.fullName,
      companyName: data.companyName,
      email: data.email,
      phone: data.phone,
      teamSize: data.teamSize,
    };

    console.log('Lead Payload:', payload);

    // Skip if no backend URL configured
    if (!APP_CONFIG.API_BASE_URL || APP_CONFIG.API_BASE_URL.includes('your-backend')) {
      console.warn('Backend API not configured. Skipping API call.');
      return { success: true, data: { leadId: `lead-${Date.now()}` } };
    }

    try {
      const response = await fetch(`${APP_CONFIG.API_BASE_URL}/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit lead');
      }

      return await response.json();
    } catch (error) {
      console.error('Error submitting lead:', error);
      // Return mock lead ID even on error so Calendly can still open
      console.warn('Backend unavailable, proceeding with mock lead ID');
      return { success: true, data: { leadId: `lead-${Date.now()}` } };
    }
  };

  // Send Calendly booking to backend
  const submitCalendlyBooking = async (bookingData) => {
    console.log('🚀 Submitting booking to backend:', bookingData);

    if (!APP_CONFIG.API_BASE_URL || APP_CONFIG.API_BASE_URL.includes('your-backend')) {
      console.warn('Backend API not configured. Skipping booking API call.');
      return { success: true };
    }

    try {
      const response = await fetch(`${APP_CONFIG.API_BASE_URL}/leads/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit booking');
      }

      return await response.json();
    } catch (error) {
      console.error('Error submitting booking:', error);
      throw error;
    }
  };

  // Handle Calendly booking event
  const handleCalendlyEvent = useCallback((e) => {
    console.log('📨 ========== CALENDLY EVENT ==========');
    console.log('📨 Event:', e.data?.event);
    console.log('📨 Full data:', JSON.stringify(e.data, null, 2));
    console.log('========================================');

    // Check for Calendly event scheduled
    if (e.data?.event === 'calendly.event_scheduled' || e.data?.event?.includes('scheduled')) {
      console.log('✅ Calendly booking detected!');

      // Prevent duplicate processing
      if (bookingCompletedRef.current) {
        console.log('⚠️ Booking already processed, skipping duplicate');
        return;
      }
      bookingCompletedRef.current = true;

      const leadId = leadIdRef.current;
      const formData = currentFormDataRef.current;

      if (!leadId) {
        console.error('❌ No leadId found');
        toast.error('Booking confirmed! Check your email for details.', {
          style: { background: 'rgba(10, 10, 15, 0.95)', color: '#fff' },
        });
        return;
      }

      console.log('📋 Processing booking for leadId:', leadId);

      // Try to extract time from Calendly payload
      const payload = e.data.payload || e.data;
      let startTime = null;
      let endTime = null;
      let eventUri = null;

      // Try different payload structures
      if (payload.event?.start_time?.dateTime) {
        startTime = payload.event.start_time.dateTime;
        endTime = payload.event.end_time?.dateTime;
        eventUri = payload.event.uri;
      } else if (payload.event?.start_time) {
        startTime = payload.event.start_time;
        endTime = payload.event.end_time;
        eventUri = payload.event.uri;
      } else if (payload.start_time) {
        startTime = payload.start_time;
        endTime = payload.end_time;
        eventUri = payload.uri;
      } else if (e.data.calendlyEvent?.start_time) {
        startTime = e.data.calendlyEvent.start_time;
        endTime = e.data.calendlyEvent.end_time;
        eventUri = e.data.calendlyEvent.uri;
      }

      console.log('📅 Extracted times:', { startTime, endTime, eventUri });

      // Create booking - even if we don't have times, we still mark as booked
      const bookingData = {
        leadId: leadId,
        eventUri: eventUri || `https://calendly.com/booking-${Date.now()}`,
        startTime: startTime || null,
        endTime: endTime || null,
        inviteeName: formData?.fullName || '',
        inviteeEmail: formData?.email || '',
      };

      submitCalendlyBooking(bookingData)
        .then((result) => {
          console.log('✅ Booking saved:', result);
          toast.success('Demo booked successfully! Check your email for confirmation.', {
            style: {
              background: 'rgba(10, 10, 15, 0.95)',
              color: '#fff',
              border: '1px solid rgba(34, 197, 94, 0.3)',
            },
            iconTheme: { primary: '#22c55e', secondary: '#fff' },
            duration: 5000,
          });

          // Close modal after successful booking
          setTimeout(() => {
            onClose();
          }, 2000);
        })
        .catch((error) => {
          console.error('❌ Booking error:', error);
          toast.error('Booking confirmed in Calendly. Check your email for details.', {
            style: { background: 'rgba(10, 10, 15, 0.95)', color: '#fff' },
          });
        });
    }
  }, [onClose]);

  // Add Calendly event listener
  useEffect(() => {
    console.log('🎧 Setting up Calendly event listener...');

    window.addEventListener('message', handleCalendlyEvent);

    return () => {
      window.removeEventListener('message', handleCalendlyEvent);
    };
  }, [handleCalendlyEvent]);

  const onSubmit = async (data) => {
    try {
      // Store form data for Calendly callback
      currentFormDataRef.current = data;
      console.log('📝 Form data stored:', data);

      // Submit lead to backend and get leadId
      const result = await submitLeadToBackend(data);
      console.log('📤 Lead submission result:', result);

      // Store leadId for Calendly callback
      leadIdRef.current = result.data?.leadId || result.leadId;
      console.log('💾 LeadId stored:', leadIdRef.current);

      toast.success('Demo request submitted! Please select a time slot.', {
        style: {
          background: 'rgba(10, 10, 15, 0.95)',
          color: '#fff',
          border: '1px solid rgba(59, 130, 246, 0.3)',
        },
        iconTheme: {
          primary: '#3b82f6',
          secondary: '#fff',
        },
      });

      reset();

      // Open Calendly popup
      setTimeout(() => {
        if (window.Calendly) {
          console.log('📅 Opening Calendly popup...');

          try {
            window.Calendly.initPopupWidget({
              url: APP_CONFIG.CALENDLY_URL,
              prefill: {
                name: data.fullName,
                email: data.email,
              },
            });
          } catch (calendlyError) {
            console.error('❌ Error opening Calendly:', calendlyError);
          }
        } else {
          console.warn('⚠️ Calendly not loaded');
          toast.error('Could not load scheduling calendar. Please try again.', {
            style: { background: 'rgba(10, 10, 15, 0.95)', color: '#fff' },
          });
        }
      }, 500);

    } catch (error) {
      console.error('❌ Form submission error:', error);
      toast.error('Something went wrong. Please try again.', {
        style: {
          background: 'rgba(10, 10, 15, 0.95)',
          color: '#fff',
        },
      });
    }
  };

  const inputClasses = (hasError) => `
    w-full bg-white border rounded-xl px-4 py-3 pl-11 text-slate-900 placeholder-slate-400
    focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/50 focus:border-[#3b82f6]/50
    transition-all duration-300
    ${hasError ? 'border-red-500/50' : 'border-slate-200 hover:border-slate-300'}
  `;

  const formFields = [
    { name: 'fullName', label: 'Full Name', type: 'text', icon: User, placeholder: 'John Doe', validation: { required: 'Full name is required' } },
    { name: 'companyName', label: 'Company Name', type: 'text', icon: Building2, placeholder: 'Acme Inc.', validation: { required: 'Company name is required' } },
    { name: 'email', label: 'Email Address', type: 'email', icon: Mail, placeholder: 'john@company.com', validation: { required: 'Email is required', pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email address' } } },
    { name: 'phone', label: 'Phone Number', type: 'tel', icon: Phone, placeholder: '+1 (555) 000-0000', validation: { required: 'Phone number is required', pattern: { value: /^[\d\s\-+()]{10,}$/, message: 'Invalid phone number' } } },
    { name: 'teamSize', label: 'Team Size', type: 'select', icon: Users, options: [
      { value: '', label: 'Select team size' },
      { value: '1-5', label: '1-5 employees' },
      { value: '6-20', label: '6-20 employees' },
      { value: '21-50', label: '21-50 employees' },
      { value: '51-200', label: '51-200 employees' },
      { value: '200+', label: '200+ employees' },
    ], validation: { required: 'Please select team size' } },
    { name: 'message', label: 'Message (Optional)', type: 'textarea', icon: MessageSquare, placeholder: 'Tell us about your needs...', validation: {} },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-dark-950/90 backdrop-blur-md"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="relative bg-white rounded-3xl overflow-hidden shadow-2xl">
              {/* Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#3b82f6]/5 via-transparent to-transparent pointer-events-none" />

              {/* Header */}
              <div className="relative p-6 pb-0">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 transition-colors rounded-lg hover:bg-slate-100 cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#3b82f6] mb-4">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2 font-title">Book Your Demo</h2>
                  <p className="text-slate-500 font-body">Schedule a 30-minute personalized demo with our team</p>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit(onSubmit)} className="relative p-6 pt-0 space-y-4">
                {formFields.map((field) => (
                  <div key={field.name}>
                    <label className="block text-sm font-medium text-slate-700 mb-2 font-body">
                      {field.label}
                    </label>
                    <div className="relative">
                      <field.icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      {field.type === 'select' ? (
                        <select
                          {...register(field.name, field.validation)}
                          className={inputClasses(errors[field.name])}
                        >
                          {field.options.map((option) => (
                            <option key={option.value} value={option.value} className="bg-white text-slate-900">
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : field.type === 'textarea' ? (
                        <textarea
                          {...register(field.name, field.validation)}
                          placeholder={field.placeholder}
                          rows={3}
                          className={`${inputClasses(errors[field.name])} resize-none`}
                        />
                      ) : (
                        <input
                          type={field.type}
                          {...register(field.name, field.validation)}
                          placeholder={field.placeholder}
                          className={inputClasses(errors[field.name])}
                        />
                      )}
                    </div>
                    {errors[field.name] && (
                      <p className="mt-1 text-sm text-red-400">{errors[field.name].message}</p>
                    )}
                  </div>
                ))}

                <Button
                  type="submit"
                  variant="primary"
                  className="w-full mt-6 py-4"
                  loading={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Schedule Demo'}
                </Button>

                <p className="text-center text-xs text-slate-400 mt-4 font-body">
                  By submitting, you agree to our Terms of Service and Privacy Policy
                </p>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BookDemoModal;